import { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordStrengthProps {
  password: string;
  className?: string;
}

interface StrengthCriteria {
  label: string;
  met: boolean;
}

function getPasswordStrength(password: string): {
  score: number;
  label: string;
  criteria: StrengthCriteria[];
} {
  const criteria: StrengthCriteria[] = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Contains a number', met: /\d/.test(password) },
    { label: 'Contains special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const score = criteria.filter((c) => c.met).length;

  let label = 'Very weak';
  if (score === 5) label = 'Strong';
  else if (score === 4) label = 'Good';
  else if (score === 3) label = 'Fair';
  else if (score === 2) label = 'Weak';

  return { score, label, criteria };
}

export function PasswordStrength({ password, className }: PasswordStrengthProps) {
  const { score, label, criteria } = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  if (!password) return null;

  const getStrengthColor = () => {
    if (score <= 1) return 'bg-destructive';
    if (score === 2) return 'bg-orange-500';
    if (score === 3) return 'bg-yellow-500';
    if (score === 4) return 'bg-lime-500';
    return 'bg-green-500';
  };

  const getLabelColor = () => {
    if (score <= 1) return 'text-destructive';
    if (score === 2) return 'text-orange-500';
    if (score === 3) return 'text-yellow-600';
    if (score === 4) return 'text-lime-600';
    return 'text-green-600';
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Password strength</span>
          <span className={cn('font-medium', getLabelColor())}>{label}</span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors',
                level <= score ? getStrengthColor() : 'bg-muted'
              )}
            />
          ))}
        </div>
      </div>

      {/* Criteria checklist */}
      <div className="grid grid-cols-1 gap-1">
        {criteria.map((item) => (
          <div
            key={item.label}
            className={cn(
              'flex items-center gap-1.5 text-xs transition-colors',
              item.met ? 'text-green-600' : 'text-muted-foreground'
            )}
          >
            {item.met ? (
              <Check className="h-3 w-3" />
            ) : (
              <X className="h-3 w-3" />
            )}
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
